import React, { useState } from "react";
import CustomToast from "../../toastify/CustomToast";
import { EMAIL_CHECK } from "../../constants/constants";
import axiosinstance from "../../services/axiosinstance";
import EndPoints from "../../services/EndPoints";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { auth, RecaptchaVerifier } from "../../module/firebase";
import { signInWithPhoneNumber } from "firebase/auth";
import CommonTextInput from "../../components/common/commonTextInput/CommonTextInput";

const DeleteMyApp = () => {

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    reason: "",
    additionalComments: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showOTPField, setShowOTPField] = useState(false);
  const [OTP, setOTP] = useState("");
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [selCountryCode, setSelCountryCode] = useState("91");
  const [selCountryExpectedLength, setSelCountryExpectedLength] = useState(0);
  const [resendTimer, setResendTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const setupRecaptcha = () => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(auth, "recaptcha-container", {
        size: "invisible",
        callback: () => {},
        "expired-callback": () => {},
      });
      window.recaptchaVerifier.render();
    }
    return window.recaptchaVerifier;
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      CustomToast.error("Please enter your name.");
      return false;
    }
    if (!formData.email.trim()) {
      CustomToast.error("Please enter your email address.");
      return false;
    }
    if (!EMAIL_CHECK.test(formData.email)) {
      CustomToast.error("Please enter a valid email address.");
      return false;
    }
    if (!formData.phoneNumber.trim()) {
      CustomToast.error("Please enter your phone number with dial code.");
      return false;
    }
    if (!formData.reason) {
      CustomToast.error("Please select a reason for deletion.");
      return false;
    }
    return true;
  };

  const startResendTimer = () => {
    setResendTimer(60);
    setCanResend(false);
  };

  const sendOTP = async (e) => {
    if (e) e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setupRecaptcha();
    const appVerifier = setupRecaptcha();
    const fullPhoneNumber = "+" + formData.phoneNumber.replace(/[^0-9]/g, "");

    signInWithPhoneNumber(auth, fullPhoneNumber, appVerifier)
      .then((result) => {
        setConfirmationResult(result);
        setShowOTPField(true);
        startResendTimer();
        CustomToast.success("OTP sent successfully to your phone!");
      })
      .catch((error) => {
        console.error("SMS not sent", error);
        CustomToast.error("Failed to send OTP. Please check your phone number.");
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleResendOTP = () => {
    if (!canResend) return;
    setOTP("");
    sendOTP(null);
  };

  const verifyOTPAndSubmit = async () => {
    if (OTP?.trim()?.length === 0) {
      return CustomToast.error("Please enter OTP");
    }

    if (!confirmationResult) {
      return CustomToast.error("OTP session expired. Please try again.");
    }

    setIsSubmitting(true);
    confirmationResult
      .confirm(OTP)
      .then(async () => {
        // OTP verified, now submit the deletion request
        try {
          const response = await axiosinstance.post(EndPoints.deleteMyApp, {
            name: formData.name.trim(),
            email: formData.email.trim(),
            phone_number: formData.phoneNumber.trim(),
            reason: formData.reason,
            additional_comments: formData.additionalComments.trim(),
          });

          CustomToast.success(
            response?.data?.message || "Your deletion request has been submitted successfully. We will process your request within 30 days."
          );

          // Reset form
          setFormData({
            name: "",
            email: "",
            phoneNumber: "",
            reason: "",
            additionalComments: "",
          });
          setShowOTPField(false);
          setOTP("");
          setConfirmationResult(null);
        } catch (error) {
          CustomToast.error(
            error?.response?.data?.message ||
              "Failed to submit deletion request. Please try again."
          );
        }
      })
      .catch((err) => {
        console.error("OTP Verification Failed", err);
        CustomToast.error("Invalid OTP. Please try again.");
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  React.useEffect(() => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(auth, "recaptcha-container", {
        size: "invisible",
        callback: () => {},
        "expired-callback": () => {},
      });
      window.recaptchaVerifier.render();
    }
  }, []);

  React.useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  return (
    <>
      <div id="recaptcha-container"></div>
      <div style={{ minHeight: "100vh", backgroundColor: "#f8f9fa", padding: "40px 20px" }}>
        <div className="container" style={{ maxWidth: "800px" }}>
          <div className="card shadow-sm">
            <div className="card-body p-4 p-md-5">
              <div className="text-center mb-4">
                <h2 className="mb-3">Account & Data Deletion Request</h2>
                <p className="text-muted">
                  We're sorry to see you go. Please fill out the form below to request
                  deletion of your account and associated data.
                </p>
              </div>

                      <form onSubmit={sendOTP}>
                      {!showOTPField && (
                        <>
                      {/* Name Field */}
                      <div className="mb-3">
                        <label htmlFor="name" className="form-label">
                          Full Name <span style={{ color: "red" }}>*</span>
                        </label>
                        <input
                          id="name"
                          type="text"
                          placeholder="Enter your full name"
                          value={formData.name}
                          onChange={(e) => handleInputChange("name", e.target.value)}
                          className="form-control"
                          disabled={showOTPField}
                        />
                      </div>

                      {/* Email Field */}
                      <div className="mb-3">
                        <label htmlFor="email" className="form-label">
                          Email Address <span style={{ color: "red" }}>*</span>
                        </label>
                        <input
                          id="email"
                          type="email"
                          placeholder="Enter your email address"
                          value={formData.email}
                          onChange={(e) => handleInputChange("email", e.target.value)}
                          className="form-control"
                          disabled={showOTPField}
                        />
                        <small className="text-muted">
                          Please use the email associated with your account
                        </small>
                      </div>

                      {/* Phone Number Field */}
                      <div className="mb-3">
                        <label htmlFor="phoneNumber" className="form-label">
                          Phone Number <span style={{ color: "red" }}>*</span>
                        </label>
                        <PhoneInput
                          country={""}
                          inputStyle={{ 
                            width: "100%", 
                            height: 45,
                            fontSize: "15px",
                            borderRadius: "6px",
                            border: "1px solid #dee2e6",
                            paddingLeft: "60px"
                          }}
                          buttonStyle={{
                            border: "1px solid #dee2e6",
                            borderRadius: "6px 0 0 6px",
                            backgroundColor: "#fff",
                            padding: "0 8px"
                          }}
                          dropdownStyle={{
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                            border: "1px solid #dee2e6",
                            maxHeight: "250px"
                          }}
                          searchStyle={{
                            width: "90%",
                            margin: "8px auto",
                            padding: "8px 12px",
                            borderRadius: "6px",
                            border: "1px solid #dee2e6",
                            fontSize: "14px"
                          }}
                          value={formData.phoneNumber}
                          onChange={(inputPhone, countryData) => {
                            handleInputChange("phoneNumber", inputPhone);
                            setSelCountryExpectedLength(
                              countryData?.format?.replace(/[()\s+\-]/g, "")?.length
                            );
                            setSelCountryCode(countryData?.dialCode);
                          }}
                          countryCodeEditable={false}
                          enableSearch={true}
                          disabled={showOTPField}
                          searchPlaceholder="Search country..."
                        />
                        <small className="text-muted">
                          We will send an OTP to verify your phone number
                        </small>
                      </div>

                      {/* Reason Dropdown */}
                      <div className="mb-3">
                        <label htmlFor="reason" className="form-label">
                          Reason for Deletion <span style={{ color: "red" }}>*</span>
                        </label>
                        <select
                          id="reason"
                          className="form-select"
                          value={formData.reason}
                          onChange={(e) => handleInputChange("reason", e.target.value)}
                          disabled={showOTPField}
                        >
                          <option value="">Select a reason</option>
                          <option value="No longer need the service">
                            No longer need the service
                          </option>
                          <option value="Privacy concerns">Privacy concerns</option>
                          <option value="Found a better alternative">
                            Found a better alternative
                          </option>
                          <option value="Too expensive">Too expensive</option>
                          <option value="Difficult to use">Difficult to use</option>
                          <option value="Technical issues">Technical issues</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>

                      {/* Additional Comments */}
                      <div className="mb-4">
                        <label htmlFor="additionalComments" className="form-label">
                          Additional Comments (Optional)
                        </label>
                        <textarea
                          id="additionalComments"
                          className="form-control"
                          rows={5}
                          maxLength={500}
                          placeholder="Please share any additional feedback or comments..."
                          value={formData.additionalComments}
                          onChange={(e) =>
                            handleInputChange("additionalComments", e.target.value)
                          }
                          disabled={showOTPField}
                        />
                        <small className="text-muted">
                          {formData.additionalComments.length}/500 characters
                        </small>
                      </div>
                        </>
                      )}

                      {/* OTP Field - Only shown after OTP is sent */}
                      {showOTPField && (
                        <>
                          <div className="mb-3">
                            <div className="alert alert-info" style={{ borderLeft: "4px solid #0dcaf0" }}>
                              <p className="mb-1">
                                <strong>OTP sent to:</strong> +{formData.phoneNumber}
                              </p>
                              <small>Please enter the 6-digit code to verify your phone number</small>
                            </div>
                          </div>
                          <div className="mb-4">
                            <label htmlFor="otp" className="form-label">
                              Enter OTP <span style={{ color: "red" }}>*</span>
                            </label>
                            <CommonTextInput
                              name="otp"
                              value={OTP}
                              className="form-control"
                              id="otp"
                              maxLength={6}
                              placeholder="Enter 6-digit OTP"
                              onChange={({ target }) => setOTP(target?.value)}
                            />
                            <div className="d-flex justify-content-between align-items-center mt-2">
                              <small className="text-muted">
                                Please enter the OTP sent to your phone number
                              </small>
                              {canResend ? (
                                <button
                                  type="button"
                                  onClick={handleResendOTP}
                                  className="btn btn-link btn-sm p-0"
                                  style={{ textDecoration: "none" }}
                                >
                                  Resend OTP
                                </button>
                              ) : (
                                <small className="text-muted">
                                  Resend in {resendTimer}s
                                </small>
                              )}
                            </div>
                          </div>
                        </>
                      )}

                      {/* Information Box */}
                      <div
                        className="alert alert-warning mt-4"
                        role="alert"
                        style={{ borderLeft: "4px solid #ffc107" }}
                      >
                        <h6 className="alert-heading">
                          <i className="fa fa-exclamation-triangle me-2"></i>
                          Important Information
                        </h6>
                        <ul className="mb-0" style={{ fontSize: "0.9rem" }}>
                          <li>
                            Your account and all associated data will be permanently deleted.
                          </li>
                          <li>This action cannot be undone.</li>
                          <li>
                            The deletion process may take up to 30 days to complete.
                          </li>
                          <li>
                            You will receive a confirmation email once your request is processed.
                          </li>
                        </ul>
                      </div>

                      {/* Submit Button */}
                      <div className="d-flex justify-content-center mt-4 mb-3">
                        {!showOTPField ? (
                          <button
                            type="submit"
                            className="btn btn-lg"
                            style={{
                              backgroundColor: "#fc374e",
                              color: "#fff",
                              minWidth: "200px",
                            }}
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                                Sending OTP...
                              </>
                            ) : (
                              "Send OTP"
                            )}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={verifyOTPAndSubmit}
                            className="btn btn-lg"
                            style={{
                              backgroundColor: "#fc374e",
                              color: "#fff",
                              minWidth: "200px",
                            }}
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                                Verifying...
                              </>
                            ) : (
                              "Verify OTP & Submit"
                            )}
                          </button>
                        )}
                      </div>
                    </form>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default DeleteMyApp;
